import { cn } from '@crowdex/ui/lib/utils';
import { type ReactNode, useEffect, useRef } from 'react';
import type { ConversationHeaderProps } from '@/components/desktop-shell/conversation-header';
import { AllAppsPage, setAllAppsOpen, useAllAppsOpen } from '@/components/sidebar/all-apps';
import { ShellSearchScreen } from '@/components/sidebar/search-dialog';
import { useShellNavigation, useSidebarWaiting } from '@/components/sidebar/shell-navigation';
import { useAssistantMobileTitle } from '@/features/assistants/assistant-heading';
import { NOTIFICATION_OPENED_EVENT } from '@/features/notifications/notifications';
import { NotificationsFeed } from '@/features/notifications/notifications-button';
import { resolveDesktopTheme } from '@/lib/app-settings';
import { selectWorktreeSourceRoot } from '@/lib/desktop-shell/conversation-worktree';
import { setSidebarSearchOpen, useSidebarSearch } from '@/lib/desktop-shell/sidebar-search';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { useWorkspaceName } from '@/lib/desktop-shell/use-workspace-name';
import { MobileConversation } from './mobile-conversation';
import { DRAWER_OFFSET, DRAWER_WIDTH, MobileDrawer } from './mobile-drawer';
import { MobileLibraryPage } from './mobile-library-page';
import { MobileSummaryPage } from './mobile-summary-page';
import { releasesSwipe, useHorizontalDrag } from './use-horizontal-drag';
import { screenLayer, useMobileLayers } from './use-mobile-layers';
import { useSheetSwipeDown } from './use-sheet-swipe-down';

/**
 * Crowdex on a phone: an app of its own around the shell's conversation (the same chat, side
 * panel and dialogs as the desktop layout). The conversation is the app's screen (a new one to
 * start); the drawer under it (mobile 2), shown by its header's button or a swipe from the left
 * edge, replaces the tab bar. From the drawer, All apps (2b), the notifications (46) and search
 * (18) are pushed full screen, and an app opens full screen in the conversation's panel. Each
 * is a history entry: the phone's back closes the one on top.
 */
export function MobileShell({
  email,
  conversationTitle,
  conversationHeader,
  started,
  conversation,
  sidePanel,
  dialogs,
}: {
  email: string;
  conversationTitle: string;
  /** The conversation header's usage and ⋮ actions, shown by the phone's own header. */
  conversationHeader?: ConversationHeaderProps;
  started: boolean;
  conversation: ReactNode;
  sidePanel: ReactNode;
  dialogs: ReactNode;
}) {
  const navigation = useShellNavigation();
  const appSettings = useDesktopShellStore((state) => state.appSettings);
  const workspace = useDesktopShellStore((state) => state.workspace);
  const worktreeSourceRoot = useDesktopShellStore(selectWorktreeSourceRoot);
  const projectRoot = worktreeSourceRoot ?? workspace;
  const conversationProject = useWorkspaceName(projectRoot);
  // A project's own conversation says the project in the header (mobile 2d, lot 6).
  const chatId = useDesktopShellStore((state) => state.chatId);
  const projectTitle = useAssistantMobileTitle(chatId);
  // Every sheet is a history entry too: the phone's back closes it, not the app.
  const { layers, push, pop, leave } = useMobileLayers({ sheets: true });
  const drawerOpen = layers.includes('drawer');
  // The screen on top, under the sheets open over it.
  const top = screenLayer(layers);
  const pushed = top === 'notifications' || top === 'search' || top === 'summary' ? top : null;
  const allAppsOpen = useAllAppsOpen();
  // All apps opens from anywhere (a tile, ⌘K): its layer follows it, and its back closes it.
  useEffect(() => {
    if (allAppsOpen && !layers.includes('apps')) push('apps');
    else if (!allAppsOpen && top === 'apps') pop();
  }, [allAppsOpen, layers, pop, push, top]);
  useEffect(() => {
    if (!layers.includes('apps')) setAllAppsOpen(false);
  }, [layers]);
  const waiting = useSidebarWaiting();
  const attention =
    waiting.size > 0 || navigation.threads.some((thread) => thread.actionRequired === true);
  useSheetSwipeDown();
  // Search opened from anywhere (⌘K, the palette, a page's command) is the phone's screen: the
  // desktop's dialog is not mounted here.
  const searchRequested = useSidebarSearch().open;
  useEffect(() => {
    if (!searchRequested) return;
    setSidebarSearchOpen(false);
    if (top !== 'search') push('search');
  }, [push, searchRequested, top]);
  // The panel opened from the drawer (a project's settings, while its conversation shows) is
  // shown: the drawer and its sheets get out of its way.
  const sidePanelOpen = useDesktopShellStore((state) => state.sidePanelOpen);
  const panelWasOpen = useRef(sidePanelOpen);
  useEffect(() => {
    const opened = sidePanelOpen && !panelWasOpen.current;
    panelWasOpen.current = sidePanelOpen;
    if (opened && drawerOpen) leave();
  }, [drawerOpen, leave, sidePanelOpen]);
  // The conversation beside the open drawer follows a finger dragging it back over the drawer.
  const screenRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLButtonElement>(null);
  const settleScreen = () => {
    const screen = screenRef.current;
    if (screen === null) return;
    screen.style.transition = '';
    // Back to the open drawer's place (its style, which React does not set again); once the
    // drawer closes, React clears it.
    screen.style.transform = `translateX(${DRAWER_OFFSET})`;
  };
  useHorizontalDrag(backdropRef, {
    enabled: drawerOpen,
    accepts: () => true,
    allows: (dx) => dx < 0,
    onDrag: (dx) => {
      const screen = screenRef.current;
      if (screen === null) return;
      screen.style.transition = 'none';
      screen.style.transform = `translateX(max(0px, calc(${DRAWER_OFFSET} + ${dx}px)))`;
    },
    onRelease: (dx, velocity) => {
      settleScreen();
      if (releasesSwipe({ dx, width: DRAWER_WIDTH, velocity })) pop();
    },
    onCancel: settleScreen,
  });

  // A notification opened elsewhere (a toast, the service worker) shows its conversation.
  useEffect(() => {
    const show = () => leave();
    window.addEventListener(NOTIFICATION_OPENED_EVENT, show);
    return () => window.removeEventListener(NOTIFICATION_OPENED_EVENT, show);
  }, [leave]);

  return (
    <div
      className="desktop-shell mobile-shell relative flex h-dvh flex-col overflow-clip bg-desktop-sidebar text-foreground"
      data-theme={resolveDesktopTheme(appSettings.theme)}
    >
      {/* Clipped, never scrolled: a view focusing itself off screen would shift the screens. */}
      <div className="relative min-h-0 flex-1 overflow-clip">
        <MobileDrawer
          account={{ name: appSettings.displayName, email }}
          onLeave={leave}
          onOpenNotifications={() => push('notifications')}
          onOpenSearch={() => push('search')}
          open={top === 'drawer'}
        />
        <div
          className={cn(
            'absolute inset-0 z-20 overflow-clip transition-transform duration-300 ease-[cubic-bezier(0.2,0.75,0.3,1)]',
            drawerOpen && 'rounded-l-window shadow-2xl',
          )}
          ref={screenRef}
          style={drawerOpen ? { transform: `translateX(${DRAWER_OFFSET})` } : undefined}
        >
          <div
            className="absolute inset-0"
            inert={drawerOpen || pushed !== null || allAppsOpen || undefined}
          >
            <MobileConversation
              conversation={conversation}
              drawerAttention={attention}
              header={conversationHeader}
              onOpenDrawer={() => push('drawer')}
              onOpenNotifications={() => push('notifications')}
              onOpenSearch={() => push('search')}
              onOpenSummary={() => push('summary')}
              open
              sidePanel={sidePanel}
              started={started}
              projectHeading={projectTitle}
              subtitle={conversationProject}
              title={started ? conversationTitle || 'Conversation' : 'New conversation'}
            />
          </div>
          {drawerOpen && (
            <button
              aria-label="Close the drawer"
              className="absolute inset-0 z-30 bg-overlay-backdrop/40"
              onClick={pop}
              ref={backdropRef}
              type="button"
            />
          )}
        </div>
        {allAppsOpen && (
          <div className="absolute inset-0 z-40">
            <AllAppsPage density="phone" onLeave={leave} />
          </div>
        )}
        {pushed === 'notifications' && (
          <div className="absolute inset-0 z-40 flex flex-col bg-background">
            <NotificationsFeed density="phone" onBack={pop} onOpened={() => leave()} />
          </div>
        )}
        {pushed === 'summary' && <MobileSummaryPage onBack={pop} />}
        {pushed === 'search' && (
          <div className="absolute inset-0 z-40">
            <ShellSearchScreen onClose={pop} onLeave={leave} />
          </div>
        )}
        <MobileLibraryPage />
      </div>
      {dialogs}
    </div>
  );
}
